export interface Density {
  xs: number[];
  ys: number[];
  bandwidth: number;
}

function mean(v: number[]): number {
  return v.reduce((a, b) => a + b, 0) / v.length;
}

function stdDev(v: number[]): number {
  if (v.length < 2) return 0;
  const m = mean(v);
  return Math.sqrt(v.reduce((a, b) => a + (b - m) ** 2, 0) / (v.length - 1));
}

const normPdf = (u: number) => Math.exp(-0.5 * u * u) / Math.sqrt(2 * Math.PI);

/**
 * Gaussian kernel density estimate. Bandwidth defaults to Silverman's
 * rule of thumb. Returns `points` samples across the data range (padded by
 * ~3 bandwidths) with the estimated density at each.
 */
export function gaussianKDE(
  values: number[],
  points = 64,
  bandwidth?: number,
): Density {
  const v = values.filter((n) => Number.isFinite(n));
  if (v.length === 0) return { xs: [], ys: [], bandwidth: 0 };

  const sd = stdDev(v);
  const h =
    bandwidth ?? Math.max(1e-9, 1.06 * (sd || 1) * v.length ** (-1 / 5));
  const min = Math.min(...v) - 3 * h;
  const max = Math.max(...v) + 3 * h;
  const xs: number[] = [];
  const ys: number[] = [];
  const step = (max - min) / (points - 1 || 1);

  for (let i = 0; i < points; i++) {
    const x = min + i * step;
    let sum = 0;
    for (const xi of v) sum += normPdf((x - xi) / h);
    xs.push(x);
    ys.push(sum / (v.length * h));
  }
  return { xs, ys, bandwidth: h };
}
